import { Injectable, inject } from '@angular/core';
import { RestService } from './rest.service';
import { BehaviorSubject } from 'rxjs';
import { CohortAverages } from '../results/results-placeholders';
import { ErrorReportingService } from './error-reporting.service';
import { SessionService } from './session.service';

export interface Score {
  total_score?: number;
  total_score_relative_withoutFactor?: number;
  total_score_relative_withoutFactor_stretched?: number;
  total_score_relative?: number;
  perfectCorpAllScore?: number;
  analysis?: any[];
  created?: Date | string;
}

export interface FullResultsData {
  userInfo: {
    images: {
      front: string;
    };
    user: any;
  };
  score: Score;
  scanResults: any;
  maskImages: { [key: string]: string };
  /** new-test modules, returned per-scan (by guid) — reliable source. */
  symptoms?: any;
  cognitive?: any;
  sense?: any;
  historicalData?: {
    scores: Score[];
    previousSelfieScan: any;
  };
  created: string;
}

export interface UserDataScan {
  guid: string;
  createdAt: string;
  symptoms: any;
  cognitive: any;
  sense: any;
}

export interface UserData {
  data: UserDataScan[];
}

/** The inputs the results screen last rendered, kept in memory so coming back
 *  to the same scan paints at once instead of re-running the whole load. */
export interface ResultsSnapshot {
  scanId: string;
  uid: number | undefined;
  full: FullResultsData;
  userData: UserData | null;
  cohort: CohortAverages | null;
}

@Injectable({
  providedIn: 'root',
})
export class ResultService {
  private fullResultsSubject = new BehaviorSubject<FullResultsData | null>(
    null
  );
  private userDataSubject = new BehaviorSubject<UserData | null>(null);
  // Which scan the cached values above belong to. The getCurrent* cache must be
  // scan-scoped or it returns a PREVIOUS scan's results/completion for a new
  // scanId — the source of the non-deterministic "scores for tests I didn't do"
  // / overview-vs-detail desync (BUG-RESULTS-COMPLETION-DESYNC).
  private cachedFullScanId: string | null = null;
  private cachedUserDataScanId: string | null = null;

  public fullResults$ = this.fullResultsSubject.asObservable();
  public userData$ = this.userDataSubject.asObservable();

  private reporter = inject(ErrorReportingService);
  private session = inject(SessionService);

  constructor(private restService: RestService) {}

  private resultsSnapshot: ResultsSnapshot | null = null;

  /** Last rendered results for this scan, only if they belong to the signed-in
   *  account (a different account on the same device never sees them). */
  getResultsSnapshot(scanId: string): ResultsSnapshot | null {
    const s = this.resultsSnapshot;
    if (!s || s.scanId !== scanId) return null;
    if (s.uid !== this.session.session?.user?.id) return null;
    return s;
  }

  setResultsSnapshot(snap: Omit<ResultsSnapshot, 'uid'>): void {
    this.resultsSnapshot = { ...snap, uid: this.session.session?.user?.id };
  }

  async getFullResults(scanId: string): Promise<FullResultsData | undefined> {
    console.log('ResultService: getFullResults called for scanId:', scanId);

    this.reporter.logEvent('results.fullResults.start');
    try {
      const response = await this.restService.request<{
        message: string;
        data: FullResultsData;
        historicalData?: {
          scores: Score[];
          previousSelfieScan: any;
        };
        created: string;
        viewerEntitlement?: {
          isEntitled: boolean;
          isPaid: boolean;
          creditEntries: any[];
        };
      }>({
        method: 'GET',
        path: `scan/${scanId}/full-results`,
        throwError: true,
      });

      if (response?.data) {
        const fullResults: FullResultsData = {
          ...response.data,
          historicalData: response.historicalData,
          created: response.created,
        };

        // Everything below is a BEST-EFFORT side-effect and must NEVER discard a
        // successfully-fetched result. Signing in ON the results route reloads
        // mid-flight, and a torn-down subject here threw ("object unsubscribed"),
        // which the caller surfaced as the transient "Failed to load results data"
        // (a refresh fixed it because there was no login racing the second load).
        try {
          // The server may have granted/consumed free-scan credits while serving
          // this scan (free-bundle link), changing entitlement mid-session. Refresh
          // the cached session user so the full-picture surfaces (progress graph /
          // doctor / routine) unlock immediately, without a re-login.
          const ent = response.viewerEntitlement;
          const su = this.session.session?.user;
          if (ent && su) {
            su.isEntitled = ent.isEntitled;
            su.isPaid = ent.isPaid;
            su.creditEntries = ent.creditEntries;
            this.session.saveSession();
          }
        } catch (e) {
          console.warn('ResultService: entitlement refresh skipped', e);
        }

        try {
          this.fullResultsSubject.next(fullResults);
        } catch {}
        this.cachedFullScanId = scanId;
        this.reporter.logEvent('results.fullResults.ok');
        return fullResults;
      }

      return undefined;
    } catch (error: any) {
      console.error('ResultService: failed to fetch full results:', error);
      this.reporter.report(`fullResults failed status=${error?.status}`, 'results');

      if (error.status === 402) {
        console.log(
          'ResultService: payment required for scan:',
          scanId,
          'error details:',
          error.error
        );
      } else if (error.status === 401) {
        console.log('ResultService: authentication required');
      } else if (error.status === 404) {
        console.log('ResultService: scan not found:', scanId);
      }

      throw error;
    }
  }

  async getUserData(scanId: string): Promise<UserData | undefined> {
    console.log('ResultService: getUserData called');

    try {
      const response = await this.restService.request<UserData>({
        method: 'GET',
        path: `scan/${scanId}/user-data`,
        throwError: true,
      });

      if (response) {
        console.log(
          'ResultService: user data fetched successfully, scans count:',
          response.data?.length || 0
        );
        this.userDataSubject.next(response);
        this.cachedUserDataScanId = scanId;
        return response;
      }

      return undefined;
    } catch (error) {
      console.error('ResultService: failed to fetch user data:', error);
      this.reporter.report(error, 'results');
      throw error;
    }
  }

  /** Real cohort averages (server-computed from existing scans). null on failure → UI hides comparisons. */
  async getCohortAverages(age: number): Promise<CohortAverages | null> {
    try {
      const response = await this.restService.request<{ data: CohortAverages }>({
        method: 'GET',
        path: `scan/cohort-averages?age=${Number.isFinite(age) ? age : 0}`,
        throwError: true,
      });
      return response?.data ?? null;
    } catch (error) {
      console.warn('ResultService: cohort averages unavailable:', error);
      return null;
    }
  }

  async getCurrentFullResults(scanId: string): Promise<FullResultsData | null> {
    // Only reuse the cache when it belongs to THIS scan — otherwise refetch so a
    // different scan never inherits the previous scan's results/completion.
    if (this.fullResultsSubject.value && this.cachedFullScanId === scanId) {
      return this.fullResultsSubject.value;
    }

    return await this.getFullResults(scanId);
  }

  async getCurrentUserData(scanId: string): Promise<UserData | null> {
    if (this.userDataSubject.value && this.cachedUserDataScanId === scanId) {
      return this.userDataSubject.value;
    }

    return await this.getUserData(scanId);
  }

  async getTrialResults(scanId: string): Promise<{
    markableIndex: number;
    history: number[];
    dates: string[];
    vsLast: number;
    vsBaseline: number;
    startDate: string;
    checkDate: string;
    checkCount: number;
    daysSinceLast: number;
  } | undefined> {
    console.log('ResultService: getTrialResults called for scanId:', scanId);

    try {
      const response = await this.restService.request<{
        message: string;
        markableIndex: number;
        history: number[];
        dates: string[];
        vsLast: number;
        vsBaseline: number;
        startDate: string;
        checkDate: string;
        checkCount: number;
        daysSinceLast: number;
        created: string;
      }>({
        method: 'GET',
        path: `scan/${scanId}/trial-results`,
        throwError: true,
      });

      if (response) {
        console.log(
          'ResultService: trial results fetched successfully, markableIndex:',
          response.markableIndex
        );

        return {
          markableIndex: response.markableIndex,
          history: response.history,
          dates: response.dates,
          vsLast: response.vsLast,
          vsBaseline: response.vsBaseline,
          startDate: response.startDate,
          checkDate: response.checkDate,
          checkCount: response.checkCount,
          daysSinceLast: response.daysSinceLast,
        };
      }

      return undefined;
    } catch (error: any) {
      console.error('ResultService: failed to fetch trial results:', error);
      this.reporter.report(`trialResults failed status=${error?.status}`, 'results');
      throw error;
    }
  }
}
